import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Grid from '@mui/material/GridLegacy';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Toolbar,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  CheckCircle as CheckIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/apiClient';
import PageLoader from '../components/PageLoader';

/**
 * SCREEN_MAP: auto-derives which screens a role can access based on its features.
 * Each entry maps a screen label to the feature(s) required to see it.
 * `requireAll: true` means ALL listed features are needed; default is ANY one.
 * `key` looks up the display label/description in roleManagement.screens.<key>.
 */
const SCREEN_MAP = [
  { key: 'dashboard', icon: '📊', requiredFeatures: ['dashboard_read'] },
  { key: 'viewPermits', icon: '📋', requiredFeatures: ['permit_read'] },
  { key: 'createPermit', icon: '➕', requiredFeatures: ['permit_create'] },
  { key: 'searchPermits', icon: '🔍', requiredFeatures: ['permit_read'] },
  { key: 'editPermit', icon: '✏️', requiredFeatures: ['permit_update'] },
  { key: 'deletePermit', icon: '🗑️', requiredFeatures: ['permit_delete'] },
  { key: 'chalans', icon: '⚡', requiredFeatures: ['chalan_read'] },
  { key: 'createChalan', icon: '📝', requiredFeatures: ['chalan_create'] },
  { key: 'editChalan', icon: '✏️', requiredFeatures: ['chalan_update'] },
  { key: 'deleteChalan', icon: '🗑️', requiredFeatures: ['chalan_delete'] },
  { key: 'reports', icon: '📈', requiredFeatures: ['report_read'] },
  { key: 'userManagement', icon: '👥', requiredFeatures: ['user_read'] },
  { key: 'createUser', icon: '👤', requiredFeatures: ['user_create'] },
  { key: 'editUser', icon: '✏️', requiredFeatures: ['user_update'] },
  { key: 'deleteUser', icon: '🗑️', requiredFeatures: ['user_delete'] },
  { key: 'roleManagement', icon: '🔐', requiredFeatures: ['role_read'] },
  { key: 'createEditRole', icon: '🔧', requiredFeatures: ['role_create', 'role_update'], requireAny: true },
  { key: 'deleteRole', icon: '🗑️', requiredFeatures: ['role_delete'] },
  { key: 'forwarding', icon: '🔄', requiredFeatures: ['forwarding_read'] },
  { key: 'approveReject', icon: '✅', requiredFeatures: ['forwarding_update'] },
];

const ADMIN_ROLE_ONLY_FEATURES = new Set([
  'permit_delete',
  'chalan_delete',
  'user_create', 'user_delete',
  'role_create', 'role_read', 'role_update', 'role_delete',
  'forwarding_create', 'forwarding_read', 'forwarding_delete',
]);

const RoleManagement = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const isStaff = user?.is_staff === true || user?.is_superuser === true;
  const [roles, setRoles] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createRoleDialogOpen, setCreateRoleDialogOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [selectedFeaturesForRole, setSelectedFeaturesForRole] = useState([]);
  const [expandedRole, setExpandedRole] = useState(null);
  const [selectedFeatureForRole, setSelectedFeatureForRole] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState({}); // roleId -> tab index (0=Features, 1=Screen Access)

  // Update (Edit) state
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [editRoleName, setEditRoleName] = useState('');
  const [editRoleDescription, setEditRoleDescription] = useState('');

  useEffect(() => {
    fetchRoles();
    fetchFeatures();
  }, [location.key]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/roles/');
      setRoles(response.data.results || response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching roles:', err);
      setError(t('roleManagement.errorLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchFeatures = async () => {
    try {
      const response = await apiClient.get('/features/');
      setFeatures(response.data.results || response.data);
    } catch (err) {
      console.error('Error fetching features:', err);
    }
  };

  const handleCreateRole = async () => {
    try {
      setLoading(true);
      const roleData = {
        name: newRoleName.toLowerCase().replace(/\s+/g, '_'),
        description: newRoleDescription,
      };

      const response = await apiClient.post('/roles/', roleData);
      const newRole = response.data;

      // Add selected features to the new role
      for (const featureId of selectedFeaturesForRole) {
        await apiClient.post(`/roles/${newRole.id}/add-feature/`, {
          feature_id: featureId,
        });
      }

      setSuccess(t('roleManagement.successRoleCreated'));
      setCreateRoleDialogOpen(false);
      setNewRoleName('');
      setNewRoleDescription('');
      setSelectedFeaturesForRole([]);
      fetchRoles();
    } catch (err) {
      console.error('Error creating role:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || err.message || t('roleManagement.errorCreateFailed');
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFeature = async (roleId, featureId) => {
    try {
      setLoading(true);
      setError('');
      const fId = parseInt(featureId, 10);

      if (!fId || isNaN(fId)) {
        setError(t('roleManagement.errorSelectFeature'));
        setLoading(false);
        return;
      }

      const response = await apiClient.post(`/roles/${roleId}/add-feature/`, {
        feature_id: fId,
      });
      setSuccess(t('roleManagement.successFeatureAdded'));
      // Use server response to update role state (authoritative, no duplicates)
      const updatedRole = response.data?.data;
      setRoles(roles.map(role => {
        if (role.id === roleId) {
          return updatedRole ? { ...role, ...updatedRole } : role;
        }
        return role;
      }));
      // Clear selection
      setSelectedFeatureForRole(prev => ({ ...prev, [roleId]: '' }));
    } catch (err) {
      console.error('Error adding feature:', err);
      const errorMsg = err.response?.data?.error || err.message || t('roleManagement.errorAddFeatureFailed');
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFeature = async (roleId, featureId) => {
    if (window.confirm(t('roleManagement.confirmRemoveFeature'))) {
      try {
        setLoading(true);
        const response = await apiClient.post(`/roles/${roleId}/remove-feature/`, {
          feature_id: featureId,
        });
        setSuccess(t('roleManagement.successFeatureRemoved'));
        // Use server response to update role state (authoritative)
        const updatedRole = response.data?.data;
        setRoles(roles.map(role => {
          if (role.id === roleId) {
            return updatedRole ? { ...role, ...updatedRole } : role;
          }
          return role;
        }));
      } catch (err) {
        console.error('Error removing feature:', err);
        const errorMsg = err.response?.data?.error || err.message || t('roleManagement.errorRemoveFeatureFailed');
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    }
  };

  // ── Update ────────────────────────────────────────────────────────────────
  const openEditDialog = (e, role) => {
    e.stopPropagation();
    setEditingRole(role);
    setEditRoleName(role.display_name || role.name);
    setEditRoleDescription(role.description || '');
    setEditRoleDialogOpen(true);
  };

  const handleUpdateRole = async () => {
    try {
      setLoading(true);
      const response = await apiClient.patch(`/roles/${editingRole.id}/`, {
        description: editRoleDescription,
      });
      setSuccess(t('roleManagement.successRoleUpdated'));
      setRoles(roles.map(r => r.id === editingRole.id ? { ...r, ...response.data } : r));
      setEditRoleDialogOpen(false);
      setEditingRole(null);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || err.message || t('roleManagement.errorUpdateFailed');
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteRole = async (e, roleId) => {
    e.stopPropagation();
    if (!window.confirm(t('roleManagement.confirmDeleteRole'))) return;
    try {
      setLoading(true);
      await apiClient.delete(`/roles/${roleId}/`);
      setSuccess(t('roleManagement.successRoleDeleted'));
      setRoles(roles.filter(r => r.id !== roleId));
      if (expandedRole === roleId) setExpandedRole(null);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || err.message || t('roleManagement.errorDeleteFailed');
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (roleName) => {
    const colors = {
      'admin': '#dc3545',
      'operator': '#007bff',
      'supervisor': '#ffc107',
      'user': '#28a745',
    };
    return colors[roleName] || '#6c757d';
  };

  const filteredRoles = roles.filter((role) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (role.name || '').toLowerCase().includes(q) ||
      (role.display_name || '').toLowerCase().includes(q) ||
      (role.description || '').toLowerCase().includes(q) ||
      (role.features || []).some((f) =>
        ((typeof f === 'string' ? f : f?.name) || '').toLowerCase().includes(q)
      )
    );
  });

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Toolbar sx={{ pl: 0, pr: 0, mb: 3, gap: 1, flexWrap: { xs: "wrap", md: "nowrap" }, alignItems: "stretch" }}>
        <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: 'bold', width: { xs: '100%', md: 'auto' } }}>
          {t('roleManagement.pageTitle')}
        </Typography>
        <TextField
          size="small"
          placeholder={t('roleManagement.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mr: { xs: 0, md: 2 }, minWidth: 0, flex: { xs: '1 1 100%', sm: '1 1 220px', md: '0 1 300px' } }}
          InputProps={{ startAdornment: <span style={{ marginRight: 6, color: '#888' }}>🔍</span> }}
        />
        <Button
          variant="contained"
          color="success"
          startIcon={<AddIcon />}
          onClick={() => setCreateRoleDialogOpen(true)}
        >
          {t('roleManagement.createNewRole')}
        </Button>
      </Toolbar>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {loading && <PageLoader variant="cards" cards={4} />}

      {!loading && (
        <Grid container spacing={3}>
          {filteredRoles.length > 0 ? (
            filteredRoles.map((role) => (
              <Grid item xs={12} sm={6} md={6} lg={5} key={role.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                      transform: 'translateY(-4px)',
                    },
                  }}
                  onClick={() =>
                    setExpandedRole(expandedRole === role.id ? null : role.id)
                  }
                >
                  <CardHeader
                    title={role.display_name || role.name}
                    action={
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={(e) => openEditDialog(e, role)}
                          sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.6)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.15)' } }}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DeleteIcon />}
                          onClick={(e) => handleDeleteRole(e, role.id)}
                          sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.6)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.15)' } }}
                        >
                          {t('common.delete')}
                        </Button>
                      </Box>
                    }
                    sx={{
                      backgroundColor: getRoleColor(role.name),
                      color: 'white',
                      display: { xs: 'block', sm: 'flex' },
                      '& .MuiCardHeader-action': { m: 0, mt: { xs: 1, sm: 0 } },
                      '& .MuiCardHeader-title': {
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                      },
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      {role.description || t('forwardingConfig.noDescription')}
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        {t('roleManagement.featuresCount', { count: role.features.length })}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {role.features.length > 0 ? (
                          role.features.slice(0, 3).map((feature) => (
                            <Chip
                              key={feature.id}
                              label={feature.display_name}
                              size="small"
                              variant="outlined"
                              icon={<CheckIcon />}
                            />
                          ))
                        ) : (
                          <Typography variant="caption" color="textSecondary">
                            {t('roleManagement.noFeaturesAssigned')}
                          </Typography>
                        )}
                        {role.features.length > 3 && (
                          <Typography variant="caption" color="primary">
                            {t('roleManagement.moreCount', { count: role.features.length - 3 })}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {expandedRole === role.id && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        {/* ── Sub-tabs: Features | Screen Access ── */}
                        <Tabs
                          value={activeTab[role.id] ?? 0}
                          onChange={(_, v) => setActiveTab(prev => ({ ...prev, [role.id]: v }))}
                          onClick={(e) => e.stopPropagation()}
                          variant="fullWidth"
                          sx={{ mb: 2, borderBottom: '1px solid #e0e0e0' }}
                        >
                          <Tab label={t('roleManagement.featuresTab')} />
                          <Tab label={t('roleManagement.screenAccessTab')} />
                        </Tabs>

                        {/* ── Tab 0: Features ── */}
                        {(activeTab[role.id] ?? 0) === 0 && (
                          <>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                              {t('roleManagement.allAssignedFeatures')}
                            </Typography>
                            <List dense>
                              {role.features.map((feature) => {
                                const isWorkflowLocked = feature.name === 'forwarding_update' && role.in_workflow;
                                return (
                                <ListItem
                                  key={feature.id}
                                  secondaryAction={
                                    <Tooltip
                                      title={isWorkflowLocked
                                        ? t('roleManagement.workflowLockedTooltip')
                                        : ''}
                                    >
                                      <span>
                                        <Button
                                          edge="end"
                                          size="small"
                                          color="error"
                                          disabled={isWorkflowLocked}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (isWorkflowLocked) return;
                                            handleRemoveFeature(role.id, feature.id);
                                          }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </Button>
                                      </span>
                                    </Tooltip>
                                  }
                                >
                                  <ListItemIcon>
                                    <CheckIcon fontSize="small" color="success" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={feature.display_name}
                                    secondary={feature.description || null}
                                  />
                                </ListItem>
                                );
                              })}
                              {role.features.length === 0 && (
                                <Typography variant="caption" color="textSecondary" sx={{ pl: 1 }}>
                                  {t('roleManagement.noFeaturesYet')}
                                </Typography>
                              )}
                            </List>

                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                              {t('roleManagement.addFeatureLabel')}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <TextField
                                select
                                SelectProps={{ native: true }}
                                size="small"
                                sx={{ flex: 1 }}
                                value={selectedFeatureForRole[role.id] || ''}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setSelectedFeatureForRole(prev => ({
                                    ...prev,
                                    [role.id]: e.target.value
                                  }));
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <option value="">{t('roleManagement.selectFeature')}</option>
                                {features
                                  .filter(f => {
                                    if (ADMIN_ROLE_ONLY_FEATURES.has(f.name)) {
                                      // These features only make sense on the admin role
                                      return (role.name || '').toLowerCase() === 'admin';
                                    }
                                    return true;
                                  })
                                  .map((feature) => {
                                  const isAssigned = role.features.some((f) => f.id === feature.id);
                                  return (
                                    <option key={feature.id} value={feature.id} disabled={isAssigned}>
                                      {feature.display_name || feature.name}
                                      {isAssigned ? t('roleManagement.alreadyAssigned') : ''}
                                    </option>
                                  );
                                })}
                              </TextField>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<AddIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (selectedFeatureForRole[role.id]) {
                                    handleAddFeature(role.id, selectedFeatureForRole[role.id]);
                                  }
                                }}
                                disabled={!selectedFeatureForRole[role.id] || loading}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {t('common.add')}
                              </Button>
                            </Box>
                          </>
                        )}

                        {/* ── Tab 1: Screen Access (auto-derived from features) ── */}
                        {(activeTab[role.id] ?? 0) === 1 && (() => {
                          const roleFeatureNames = role.features.map(f => f.name);
                          const hasAccess = (entry) =>
                            entry.requireAny
                              ? entry.requiredFeatures.some(f => roleFeatureNames.includes(f))
                              : entry.requiredFeatures.every(f => roleFeatureNames.includes(f));
                          const accessible = SCREEN_MAP.filter(hasAccess);
                          const denied = SCREEN_MAP.filter(e => !hasAccess(e));
                          return (
                            <Box onClick={(e) => e.stopPropagation()}>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
                                {t('roleManagement.screenAccessNote')}
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{t('roleManagement.screenActionCol')}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{t('permitSearch.description')}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>{t('roleManagement.accessCol')}</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {SCREEN_MAP.map((entry) => {
                                    const allowed = hasAccess(entry);
                                    return (
                                      <TableRow key={entry.key} sx={{ bgcolor: allowed ? '#f0fff4' : '#fff8f8' }}>
                                        <TableCell>
                                          <span style={{ marginRight: 6 }}>{entry.icon}</span>
                                          {t(`roleManagement.screens.${entry.key}.label`)}
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption" color="textSecondary">
                                            {t(`roleManagement.screens.${entry.key}.desc`)}
                                          </Typography>
                                          <br />
                                          <Typography variant="caption" sx={{ color: '#888' }}>
                                            {t('roleManagement.requiresLabel', { list: entry.requiredFeatures.join(entry.requireAny ? ' OR ' : ' + ') })}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                          <Tooltip title={allowed ? t('roleManagement.accessGranted') : t('roleManagement.accessDenied')}>
                                            {allowed
                                              ? <VisibilityIcon fontSize="small" sx={{ color: '#4caf50' }} />
                                              : <VisibilityOffIcon fontSize="small" sx={{ color: '#f44336' }} />}
                                          </Tooltip>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                              <Box sx={{ mt: 1.5, display: 'flex', gap: 2 }}>
                                <Chip size="small" icon={<VisibilityIcon />} label={t('roleManagement.accessibleCount', { count: accessible.length })} color="success" variant="outlined" />
                                <Chip size="small" icon={<VisibilityOffIcon />} label={t('roleManagement.restrictedCount', { count: denied.length })} color="error" variant="outlined" />
                              </Box>
                            </Box>
                          );
                        })()}
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="textSecondary">
                    {searchQuery ? t('roleManagement.noRolesMatch', { query: searchQuery }) : t('roleManagement.noRolesFound')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Create Role Dialog */}
      <Dialog open={createRoleDialogOpen} onClose={() => setCreateRoleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#4caf50', color: 'white', fontWeight: 'bold' }}>
          {t('roleManagement.createRoleTitle')}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label={t('roleManagement.roleNameLabel')}
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            margin="normal"
            placeholder={t('roleManagement.roleNamePlaceholder')}
          />
          <TextField
            fullWidth
            label={t('permitSearch.description')}
            value={newRoleDescription}
            onChange={(e) => setNewRoleDescription(e.target.value)}
            margin="normal"
            multiline
            rows={3}
            placeholder={t('roleManagement.rolePurposePlaceholder')}
          />

          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 3, mb: 1 }}>
            {t('roleManagement.selectFeaturesLabel')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {features.map((feature) => (
              <Chip
                key={feature.id}
                label={feature.display_name}
                onClick={() => {
                  if (selectedFeaturesForRole.includes(feature.id)) {
                    setSelectedFeaturesForRole(
                      selectedFeaturesForRole.filter((id) => id !== feature.id)
                    );
                  } else {
                    setSelectedFeaturesForRole([...selectedFeaturesForRole, feature.id]);
                  }
                }}
                color={
                  selectedFeaturesForRole.includes(feature.id)
                    ? 'primary'
                    : 'default'
                }
                variant={
                  selectedFeaturesForRole.includes(feature.id)
                    ? 'filled'
                    : 'outlined'
                }
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          <Button onClick={() => setCreateRoleDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleCreateRole}
            variant="contained"
            color="success"
            disabled={loading || !newRoleName.trim()}
          >
            {t('roleManagement.createRoleBtn')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit Role Dialog */}
      <Dialog open={editRoleDialogOpen} onClose={() => setEditRoleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 'bold' }}>
          {t('roleManagement.editRoleTitle', { name: editingRole?.display_name || editingRole?.name })}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label={t('roleManagement.roleNameLabel')}
            value={editRoleName}
            margin="normal"
            disabled
            helperText={t('roleManagement.roleNameCannotChange')}
          />
          <TextField
            fullWidth
            label={t('permitSearch.description')}
            value={editRoleDescription}
            onChange={(e) => setEditRoleDescription(e.target.value)}
            margin="normal"
            multiline
            rows={3}
            placeholder={t('roleManagement.rolePurposePlaceholder')}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          <Button onClick={() => setEditRoleDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleUpdateRole}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {t('roleManagement.updateRoleBtn')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleManagement;
